import { useClientPortalAPI } from "@certn/api";
import {
  Layout,
  SecondaryTopNav,
  Container,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
  SuccessTag,
  Loader,
  NeutralTag,
  BasicButton,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { t } from "i18next";
import { useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { createIncludesTabIdMapper } from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { GroupDetailsTab } from "./GroupDetailsTab";
import { GroupUsersTab } from "./GroupUsersTab";
import { GroupPreferencesTab } from "./GroupPreferencesTab";
import { HeaderCard } from "./HeaderCard";
import { ConfigurationError } from "../../../../components/ConfigurationError";
import { PageTitle } from "../../../../components/page-title/PageTitle";
import {
  useCanUpdateGroups,
  useCanViewGroups,
} from "../../../../components/permissions/Permissions";
import { GroupDropdownMenu } from "../../components/GroupDropdownMenu";
import { UnsavedChangesModal } from "../modals/UnsavedChangesModal";

export function GroupDetailsPage() {
  const { pathname } = useLocation();
  const url = useResolvedPath("").pathname;
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [destination, setDestination] = useState("");

  const mapSelectedTabId = createIncludesTabIdMapper(
    [
      { substring: "/members", id: "members-tab" },
      { substring: "/preferences", id: "preferences-tab" },
    ],
    "details-tab"
  );

  const { clientPortalRestrictApplicantVisibility } = useFlags();
  const {
    data: group,
    isLoading: isGroupLoading,
    mutate: mutateGroup,
  } = useClientPortalAPI(
    `/api/internal/client-portal/customer-groups/${groupId}/`
  );
  const userCanUpdateGroups = useCanUpdateGroups();
  const userCanViewGroups = useCanViewGroups();

  const handleLeavePage = (direction: string) => {
    if (!hasUnsavedChanges) navigate(direction);
    //either leaving to go to group list or group overview page
    setDestination(direction);
    setShowUnsavedChangesModal(hasUnsavedChanges);
  };

  if (isGroupLoading) {
    return <Loader overlay background="background-base" />;
  }

  if (!userCanViewGroups) {
    return (
      <ConfigurationError
        message={t(
          "Hmm, it looks like you do not have permission to view this page."
        )}
      />
    );
  }

  return (
    <>
      <Layout
        layout="narrow"
        header={
          <SecondaryTopNav
            testID="back-button"
            leftIcon="close"
            onleftIconOnPress={() => {
              handleLeavePage("/groups");
            }}
          >
            <BasicButton
              onPress={() =>
                handleLeavePage(`/cases?customer_group_id=${groupId}`)
              }
              testID="open-groups-page-in-new-tab-button"
              rightIcon="open_in_new"
            >
              {t("Group page")}
            </BasicButton>
          </SecondaryTopNav>
        }
      >
        <Container
          padding={["space-3"]}
          testID="customer-group-details-page-container"
        >
          <PageTitle>{t("Group Details")}</PageTitle>
          <HeaderCard
            title={
              <div className={styles.groupCard}>
                <Text style="global/heading-large" color="text-weak">
                  {group?.name}
                </Text>
                {group?.is_active ? (
                  <SuccessTag type="subtle">{t("Active")}</SuccessTag>
                ) : (
                  <NeutralTag type="subtle">{t("Deactivated")}</NeutralTag>
                )}
              </div>
            }
            trailing={
              userCanUpdateGroups ? (
                <GroupDropdownMenu
                  mutate={mutateGroup}
                  groupId={group?.id ?? ""}
                  groupName={group?.name ?? ""}
                  isActive={group?.is_active ?? false}
                />
              ) : undefined
            }
          />
          <Tabs
            selectedKey={mapSelectedTabId(pathname)}
            aria-label="user-details-tabs"
            testID="user-details-tabs"
          >
            <TabList aria-label="Tabs">
              <Tab id="details-tab" href={`${url}`} testID="group-details-tab">
                {t("Details")}
              </Tab>
              <Tab
                id="members-tab"
                href={`${url}/members`}
                testID="group-members-tab"
              >
                {t("Members ({{userCount}})", {
                  userCount: group?.user_set?.length,
                })}
              </Tab>
              {clientPortalRestrictApplicantVisibility &&
                userCanUpdateGroups && (
                  <Tab
                    id="preferences-tab"
                    href={`${url}/preferences`}
                    testID="group-preferences-tab"
                  >
                    {t("Preferences")}
                  </Tab>
                )}
            </TabList>

            <TabPanel id={mapSelectedTabId(pathname)}>
              <Routes>
                <Route
                  path=""
                  element={
                    <GroupDetailsTab
                      setHasUnsavedChanges={setHasUnsavedChanges}
                    />
                  }
                />
                <Route path="/members" element={<GroupUsersTab />} />
                {clientPortalRestrictApplicantVisibility &&
                  userCanUpdateGroups && (
                    <Route
                      path="/preferences"
                      element={<GroupPreferencesTab />}
                    />
                  )}
              </Routes>
            </TabPanel>
          </Tabs>
        </Container>
        {showUnsavedChangesModal && (
          <UnsavedChangesModal
            onClose={() => setShowUnsavedChangesModal(false)}
            destination={destination}
          />
        )}
      </Layout>
    </>
  );
}

const styles = {
  groupCard: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1-5)",
    alignItems: "left",
  }),
};
